import { useRef, type ReactElement } from 'react';
import { areaPath, buildSeriesSummary, clamp, isFiniteNumber, isolatedPoints, linePath, linearScale, nearestIndex, niceTicks, pointPositions, smoothLinePath, valueDomain, type PathPoint } from '@vello/charts-core';
import {
  ChartEmpty,
  ChartLegend,
  ChartLive,
  ChartLoading,
  ChartTooltip,
  cascade,
  colorOf,
  cx,
  defaultFormatValue,
  distinctTicks,
  edgeAnchoredLabelStride,
  joinSummary,
  seriesDash,
  useActivePoint,
  useElementWidth,
  useMotionState,
  useTweenedValues,
  type ChartToneName,
} from './kit';
import { ChartToolbar, TargetMark, emitSelect, isChosen, targetExtent, targetSummary, type ChartSelectProps, type ChartTarget } from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';

export interface LineSeries {
  readonly key: string;
  /** The series name (legend, tooltip and the accessible summary). */
  readonly label: string;
  /** One value per label; `null` is a gap (the line breaks there). */
  readonly values: readonly (number | null)[];
  /** Mark a STATE (success / warning / danger) instead of the ordered series colour. */
  readonly tone?: ChartToneName;
}

export interface ChartBaseProps {
  /** The chart's name: the first words of the accessible label. */
  readonly title?: string;
  /** Replaces the generated accessible label entirely. */
  readonly ariaLabel?: string;
  /** Format a value for the tooltip and the summary (injected: money, count, duration …). */
  readonly formatValue?: (value: number) => string;
  /** Show the loading skeleton instead of the chart. */
  readonly loading?: boolean;
  readonly loadingLabel?: string;
  /** Localized message for the empty state (shown when there is no drawable value). */
  readonly emptyLabel?: string;
  /** `false` turns every animation off; default: on, except in a synthetic DOM. */
  readonly animate?: boolean;
  readonly className?: string;
}

export interface LineChartProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  /** The x category labels, one per point (already formatted for the market: dates, hours …). */
  readonly labels: readonly string[];
  readonly series: readonly LineSeries[];
  /** Plot height in SVG units (default 220). */
  readonly height?: number;
  /** Soft area under the FIRST series (default true). */
  readonly area?: boolean;
  /** Monotone smoothing: never overshoots the data (default true). */
  readonly smooth?: boolean;
  readonly showGrid?: boolean;
  /** Y tick labels and x labels (default true). */
  readonly showAxis?: boolean;
  /** About how many y intervals (default 4). */
  readonly yTicks?: number;
  /** Force the y domain, e.g. `[0, 100]` for a percentage. */
  readonly yDomain?: readonly [number, number];
  /** Keep zero in the y domain (default true; turn off to zoom a series that lives far from zero). */
  readonly includeZero?: boolean;
  /** Format the y tick labels (default: `formatValue`). */
  readonly formatAxis?: (value: number) => string;
  /** A goal line (and an accepted band) on the value axis; its label is part of the accessible summary (D529). */
  readonly target?: ChartTarget;
}

const FALLBACK_WIDTH = 480;
/** Width of one mono tick-label character at the micro size, in SVG units. */
const AXIS_CHAR_WIDTH = 6.8;
const MIN_X_LABEL_GAP = 64;
const TOP = 12;
const RIGHT = 12;
const X_AXIS_HEIGHT = 26;
const DOT_RADIUS = 4;

/** A multi-point line chart (optionally with a soft area), grid, axis and a crosshair tooltip reachable by keyboard. */
export function LineChart(props: LineChartProps): ReactElement {
  const height = props.height ?? 220;
  if (props.loading === true) return <ChartLoading height={height} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  const count = props.labels.length;
  const hasData = count > 0 && props.series.some((s) => s.values.slice(0, count).some(isFiniteNumber));
  if (!hasData) return <ChartEmpty height={height} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return <LineChartPlot {...props} height={height} />;
}

function LineChartPlot(props: LineChartProps & { height: number }): ReactElement {
  const { labels, series, height } = props;
  const format = props.formatValue ?? defaultFormatValue;
  const formatAxis = props.formatAxis ?? format;
  const smooth = props.smooth ?? true;
  const showGrid = props.showGrid ?? true;
  const showAxis = props.showAxis ?? true;
  const count = labels.length;

  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(count, props.onSelect !== undefined ? (i) => emitSelect(props, labels[i] ?? '') : undefined);

  const flat = series.flatMap((s) => Array.from({ length: count }, (_, i): number | null => (isFiniteNumber(s.values[i]) ? (s.values[i] as number) : null)));
  const target = distinctTicks(
    props.yDomain !== undefined
      ? niceTicks(props.yDomain[0], props.yDomain[1], props.yTicks ?? 4)
      : valueDomain([...flat, ...targetExtent(props.target)], { includeZero: props.includeZero ?? true, tickCount: props.yTicks ?? 4 }),
    formatAxis,
  );
  // The domain is tweened with the values, so the gridlines glide with the line instead of jumping.
  const tweened = useTweenedValues([...flat, target.domain[0], target.domain[1]], motion);
  const drawn = tweened.slice(0, flat.length);
  const domain: [number, number] = [tweened[flat.length] ?? target.domain[0], tweened[flat.length + 1] ?? target.domain[1]];

  const tickText = target.ticks.map((t) => formatAxis(t));
  const left = showAxis ? clamp(Math.max(...tickText.map((t) => t.length)) * AXIS_CHAR_WIDTH + 14, 30, 96) : 8;
  const bottom = showAxis ? X_AXIS_HEIGHT : 8;
  const plotWidth = Math.max(width - left - RIGHT, 20);
  const plotHeight = Math.max(height - TOP - bottom, 20);
  const baselineY = TOP + plotHeight;
  const y = linearScale(domain, [baselineY, TOP]);
  const xs = pointPositions(count, [left, left + plotWidth]);

  const pointsOf = (valuesAt: (i: number) => number | null): PathPoint[] => xs.map((x, i) => ({ x, y: valuesAt(i) === null ? null : y(valuesAt(i) as number) }));
  const layers = series.map((s, si) => ({ series: s, index: si, points: pointsOf((i) => drawn[si * count + i] ?? null) }));
  const targetAt = (si: number, i: number): number | null => flat[si * count + i] ?? null;

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      targetSummary(props.target, format),
      ...series.map((s) => buildSeriesSummary(labels.map((label, i) => ({ label, value: s.values[i] })), { ...(series.length > 1 ? { title: s.label } : {}), format })),
    ]);

  // x labels thinned to the width, never overlapping.
  const stride = edgeAnchoredLabelStride(labels, count > 1 ? plotWidth / (count - 1) : plotWidth, MIN_X_LABEL_GAP);
  const csv = (): ChartCsv => ({ header: ['', ...series.map((s) => s.label)], rows: labels.map((l, i) => [l, ...series.map((s) => (isFiniteNumber(s.values[i]) ? (s.values[i] as number) : null))]) });
  const activeIndex = active.index;
  const activeRows =
    activeIndex === null
      ? []
      : series.map((s, si) => ({ key: s.key, label: s.label, value: targetAt(si, activeIndex) === null ? '–' : format(targetAt(si, activeIndex) as number), color: colorOf(si, s.tone), y: targetAt(si, activeIndex) === null ? null : y(targetAt(si, activeIndex) as number) }));
  const activeText = activeIndex === null ? '' : `${labels[activeIndex] ?? ''}: ${activeRows.map((r) => (series.length > 1 ? `${r.label} ${r.value}` : r.value)).join(', ')}`;
  const tooltipY = activeRows.reduce<number>((min, r) => (r.y !== null && r.y < min ? r.y : min), baselineY);

  return (
    <div className={cx('vello-chart', 'vello-chart--line', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div
        ref={ref}
        className="vello-chart__plot vello-chart__focus"
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={active.onKeyDown}
        onFocus={active.onFocus}
        onBlur={active.onBlur}
      >
        <svg
          className="vello-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onClick={(e) => active.activateFromPointer(e, (x) => nearestIndex(xs, x))}
          onPointerMove={(e) => active.fromPointer(e, (x) => nearestIndex(xs, x))}
          onPointerDown={(e) => active.fromPointer(e, (x) => nearestIndex(xs, x))}
          onPointerLeave={active.onPointerLeave}
        >
          {props.target !== undefined && <TargetMark target={props.target} y={y} left={left} right={left + plotWidth} format={format} layer="band" />}
          {showGrid &&
            target.ticks.map((t) => (
              <line key={`g${t}`} className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={left} x2={left + plotWidth} y1={y(t)} y2={y(t)} />
            ))}
          {showAxis &&
            target.ticks.map((t, i) => (
              <text key={`t${t}`} className="vello-chart__tick tnum" x={left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {tickText[i]}
              </text>
            ))}
          {showAxis &&
            labels.map((label, i) =>
              i % stride === 0 ? (
                <text key={`x${i}`} className="vello-chart__tick" x={xs[i]} y={baselineY + 18} textAnchor={i === 0 && count > 1 ? 'start' : i === count - 1 && count > 1 ? 'end' : 'middle'}>
                  {label}
                </text>
              ) : null,
            )}
          {(props.area ?? true) && layers[0] !== undefined && (
            <path className="vello-chart__area vello-chart__draw" d={areaPath(layers[0].points, baselineY, { smooth })} fill={colorOf(0, layers[0].series.tone)} />
          )}
          {labels.map((label, i) =>
            isChosen(props, label) && xs[i] !== undefined ? <line key={`c${i}`} className="vello-chart__chosen-line" data-chosen="true" x1={xs[i]} x2={xs[i]} y1={TOP} y2={baselineY} /> : null,
          )}
          {activeIndex !== null && <line className="vello-chart__crosshair" x1={xs[activeIndex]} x2={xs[activeIndex]} y1={TOP} y2={baselineY} />}
          {layers.map((layer) => (
            <g key={layer.series.key}>
              <path
                className="vello-chart__line vello-chart__draw"
                d={smooth ? smoothLinePath(layer.points) : linePath(layer.points)}
               
                stroke={colorOf(layer.index, layer.series.tone)}
                {...(seriesDash(layer.index) !== undefined ? { strokeDasharray: seriesDash(layer.index) } : {})}
                style={cascade(layer.index)}
              />
              {isolatedPoints(layer.points).map((p) => (
                <circle key={`i${p.x}`} className="vello-chart__dot" cx={p.x} cy={p.y} r={DOT_RADIUS - 1} fill={colorOf(layer.index, layer.series.tone)} />
              ))}
              {(() => {
                const lastIndex = layer.points.reduce((acc, p, i) => (p.y !== null ? i : acc), -1);
                const p = lastIndex >= 0 ? layer.points[lastIndex] : undefined;
                return p !== undefined && p.y !== null && activeIndex === null && layer.points.filter((q) => q.y !== null).length > 1 ? (
                  <circle className="vello-chart__dot vello-chart__pop" cx={p.x} cy={p.y} r={DOT_RADIUS} fill={colorOf(layer.index, layer.series.tone)} />
                ) : null;
              })()}
              {layer.points.map((p, i) =>
                isChosen(props, labels[i] ?? '') && p.y !== null ? <circle key={`ring${i}`} className="vello-chart__chosen-ring" data-chosen="true" cx={p.x} cy={p.y} r={DOT_RADIUS + 2} /> : null,
              )}
              {activeIndex !== null && layer.points[activeIndex]?.y !== null && layer.points[activeIndex] !== undefined && (
                <circle className="vello-chart__dot" data-active="true" cx={layer.points[activeIndex]?.x} cy={layer.points[activeIndex]?.y ?? 0} r={DOT_RADIUS + 1} fill={colorOf(layer.index, layer.series.tone)} />
              )}
            </g>
          ))}
          {props.target !== undefined && <TargetMark target={props.target} y={y} left={left} right={left + plotWidth} format={format} layer="line" />}
        </svg>
        {activeIndex !== null && xs[activeIndex] !== undefined && (
          <ChartTooltip x={xs[activeIndex] as number} y={tooltipY} width={width} heading={labels[activeIndex] ?? ''} rows={activeRows.map((r) => ({ key: r.key, ...(series.length > 1 ? { label: r.label, color: r.color } : {}), value: r.value }))} />
        )}
      </div>
      <ChartLive text={activeText} />
      {series.length > 1 && <ChartLegend items={series.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si, s.tone), dash: seriesDash(si) }))} />}
    </div>
  );
}
